import { Stack } from '@mui/material'
import { useCallback, useState } from 'react'
import { useFieldArray } from 'react-hook-form'
import { Button, InformationModal, Resource } from 'utoniq-ui'

import {
  CreateItemInput,
  ItemThumbnailType,
  ItemType,
  MagazinePageDirection,
  SkuUnitInput,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  handleError,
  tLabel,
  tMessage,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import {
  AppDeadlineInputCell,
  ContentsAudioInputCell,
  ExpirationDateInputCell,
  IssueInputCell,
  ItemImageInputCell,
  ItemThumbnailInputCell,
  LimitIssueInputCell,
  MusicApplicationInputCell,
  PageDirectionInputCell,
  PerOrderLimitInputCell,
  PersonalInformationRequirementsInputCell,
  personalInformationRequirementsValue,
  PriceInputCell,
  PrintInputCell,
  ResaleInputCell,
  ResourceUploadCell,
  ShareInputCell,
  SkuInputCell,
  TextInputCell,
  ThroughTicketInputCell,
  TicketNumberInputCell,
  ToggleInputCell,
} from './components'
import type { PersonalInformationRequirementsValue } from './components/PersonalInformationRequirementsInputCell'

export type ItemCreateFormProps = {
  resourceType: ItemType
  fileUploadInfo: FileUploadInfo
  onSubmit: (values: CreateItemProps) => Promise<void>
}

export type CreateItemProps = Omit<
  CreateItemInput,
  'personalInformationRequirements'
> & {
  hasPrice: boolean
  hasIssuedNumber: boolean
  resource: Resource | null
  avatarResource: Resource | null
  wallpaperResource: Resource | null
  thumbnailResource: Resource | null
  thumbnailBackResource: Resource | null
  hasPurchaseLimitByUser: boolean
  hasPurchaseLimitPerOrder: boolean
  hasSku: boolean
  personalInformationRequirements: PersonalInformationRequirementsValue
  hideSalesNumber: boolean
  isDownloadable: boolean | undefined
  hasAssignedNumber: boolean
  isBothThumbnail: boolean
}

export type FileUploadInfo = {
  onDrop: (
    resourceType: ItemType,
    hasCreateResource?: boolean
  ) => Promise<Resource | undefined>
  progress: number
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().label(tLabel('consoleItemCreate.title')).max(100),
  description: o.string().when('type', {
    is: ItemType.Ticket,
    then: o.string().optional().allow(null),
    otherwise: o
      .string()
      .max(1000)
      .label(tLabel('consoleItemCreate.description')),
  }),
  resourceId: o.string().when('type', {
    switch: [
      {
        is: ItemType.Audio,
        then: o.string().label(tLabel('consoleItemCreate.resourceId.audio')),
      },
      {
        is: ItemType.Video,
        then: o
          .string()
          .label(tLabel('consoleItemCreate.resourceId.video'))
          .required(),
      },
      {
        is: ItemType.Magazine,
        then: o
          .string()
          .label(tLabel('consoleItemCreate.resourceId.magazine'))
          .required(),
      },
    ],
    otherwise: o.string().allow('').optional(),
  }),
  resource: o.object().allow(null).optional(),
  avatarResources: o.object().when('type', {
    is: ItemType.Avatar,
    then: o.object({
      avatarResourceId: o
        .string()
        .label(tLabel('consoleItemCreate.avatarResources.avatarResourceId'))
        .required(),
      voiceResourceId: o.string().allow('').optional(),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  avatarResource: o.object().allow(null).optional(),
  wallpaperResources: o.object().when('type', {
    is: ItemType.Wallpaper,
    then: o.object({
      wallpaperResourceId: o
        .string()
        .required()
        .label(
          tLabel('consoleItemCreate.wallpaperResources.wallpaperResourceId')
        ),
      audioResourceId: o.string().allow('').optional(),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  wallpaperResource: o.object().allow(null).optional(),
  hasPrice: o.boolean().optional(),
  price: o
    .number()
    .label(tLabel('consoleItemCreate.price'))
    .when('hasPrice', [
      { is: true, then: o.number().required().min(100).max(100000) },
      { is: false, then: o.number().allow(0).optional() },
    ]),
  paymentType: o.string().when('hasPrice', [
    {
      is: true,
      then: o
        .string()
        .valid(UserPaymentType.PrepaidPoint, UserPaymentType.Cash),
    },
    {
      is: false,
      then: o
        .string()
        .valid(UserPaymentType.PrepaidPoint, UserPaymentType.Cash)
        .allow(null)
        .allow('')
        .optional(),
    },
  ]),
  hasIssuedNumber: o.boolean().optional(),
  issuedNumber: o
    .number()
    .label(tLabel('consoleItemCreate.issuedNumber'))
    .when('hasIssuedNumber', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  hideSalesNumber: o.boolean().optional(),
  isBothThumbnail: o.boolean().optional(),
  hasPurchaseLimitByUser: o.boolean().optional(),
  purchaseLimitByUser: o
    .number()
    .label(tLabel('consoleItemCreate.purchaseLimitByUser'))
    .when('hasPurchaseLimitByUser', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  hasPurchaseLimitPerOrder: o.boolean().optional(),
  purchaseLimitPerOrder: o
    .number()
    .label(tLabel('consoleItemCreate.purchaseLimitPerOrder'))
    .when('hasPurchaseLimitPerOrder', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  isPremium: o
    .boolean()
    .label(tLabel('consoleItemCreate.isPremium'))
    .optional(),
  personalInformationRequirements: o.when('type', {
    is: ItemType.Ticket,
    then: o
      .string()
      .optional()
      .label('consoleItemCreate.personalInformationRequirements'),
  }),
  type: o.string(),
  resaleEnabled: o
    .boolean()
    .optional()
    .label(tLabel('consoleItemCreate.resaleEnabled')),
  hasAssignedNumber: o.boolean().optional(),
  thumbnailType: o.string().allow('').optional(),
  thumbnail: o.string().label(tLabel('consoleItemCreate.title')),
  thumbnailBack: o
    .string()
    .label(tLabel('consoleItemCreate.thumbnailBack'))
    .allow('')
    .optional(),
  metadata: o.object().when('type', {
    switch: [
      {
        is: ItemType.Merch,
        then: o.object({
          merch: o.object({
            receptionPeriod: o.object({
              endedAt: o
                .date()
                .label(
                  tLabel(
                    'consoleItemCreate.metadata.merch.receptionPeriod.endedAt'
                  )
                )
                .required(),
            }),
          }),
          ticket: o.object().optional(),
          magazine: o.object().optional(),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Ticket,
        then: o.object({
          merch: o.object().optional(),
          magazine: o.object().optional(),
          ticket: o.object({
            description: o
              .string()
              .max(1000)
              .label(tLabel('consoleItemCreate.metadata.ticket.description')),
            expirationTerm: o
              .string()
              .optional()
              .allow(null)
              .label(
                tLabel('consoleItemCreate.metadata.ticket.expirationTerm')
              ),
            note: o
              .string()
              .optional()
              .allow(null)
              .label(tLabel('consoleItemCreate.metadata.ticket.note')),
            resalePeriod: o.object().when(o.ref('....resaleEnabled'), {
              is: true,
              then: o.object({
                startedAt: o
                  .date()
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.resalePeriod.startedAt'
                    )
                  ),
                endedAt: o
                  .date()
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.resalePeriod.endedAt'
                    )
                  ),
              }),
              otherwise: o.object({
                startedAt: o
                  .date()
                  .optional()
                  .allow('')
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.resalePeriod.startedAt'
                    )
                  ),
                endedAt: o
                  .date()
                  .optional()
                  .allow('')
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.resalePeriod.endedAt'
                    )
                  ),
              }),
            }),
            assignedNumberPrefix: o
              .string()
              .when(o.ref('....hasAssignedNumber'), {
                is: true,
                then: o
                  .string()
                  .max(5)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.assignedNumberPrefix'
                    )
                  ),
                otherwise: o.string().optional().allow(null),
              }),
            startAssignedNumber: o
              .string()
              .when(o.ref('....hasAssignedNumber'), {
                is: true,
                then: o
                  .string()
                  .max(8)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.startAssignedNumber'
                    )
                  ),
                otherwise: o.string().optional().allow(null),
              }),
            isThroughTicket: o.boolean().optional().allow(null),
            throughTicket: o.array().when('isThroughTicket', {
              is: true,
              then: o.array().items(
                o.object({
                  //id of field array
                  _id: o.string().optional(),
                  name: o
                    .string()
                    .label(
                      tLabel(
                        'consoleItemCreate.metadata.ticket.throughTicket.name'
                      )
                    ),
                })
              ),
              otherwise: o.array().optional().allow(null),
            }),
            distributionEnabled: o.boolean().optional().allow(null),
            distributionEndedAt: o.date().when('distributionEnabled', {
              is: true,
              then: o
                .date()
                .label(
                  tLabel(
                    'consoleItemCreate.metadata.ticket.distributionEndedAt'
                  )
                )
                .required(),
              otherwise: o.date().optional().allow(null, ''),
            }),
          }),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Magazine,
        then: o.object({
          ticket: o.object().optional(),
          merch: o.object().optional(),
          magazine: o.object({
            pageDirection: o
              .string()
              .label(
                tLabel('consoleItemCreate.metadata.magazine.pageDirection')
              ),
          }),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Wallpaper,
        then: o.object({
          ticket: o.object().optional(),
          magazine: o.object().optional(),
          merch: o.object().optional(),
          wallpaper: o.object({
            isPrintEnabled: o.boolean().optional().allow(null),
            printCount: o.number().optional().allow(null),
            printType: o.string().optional().allow(null),
          }),
        }),
      },
    ],
    otherwise: o.object().allow(null).optional(),
  }),
  thumbnailResource: o.object().allow(null).optional(),
  thumbnailBackResource: o.object().allow(null).optional(),
  hasSku: o.boolean().optional(),
  images: o.array().items(
    o.object({
      tmpPath: o.string().optional().allow(''),
      caption: o.string().optional(),
    })
  ),
  skuSettings: o.when('hasSku', {
    is: true,
    then: o.object({
      keyName: o.string().optional().allow(null),
      units: o.array().items(
        o.object({
          _id: o.string().optional(),
          id: o.string().optional(),
          unitName: o
            .string()
            .max(100)
            .label(tLabel('consoleItemCreate.unitName')),
          price: o.number().optional().allow(null),
          stock: o
            .number()
            .optional()
            .allow('')
            //check total stock less than issuedNumber
            .custom((value: number, helpers) => {
              const [, units, , data] = helpers?.state?.ancestors || []
              if (!value) {
                return value
              }
              const issuedNumber = data?.issuedNumber || 0
              const totalItemInStock = units?.reduce(
                (currentVal: number, unit: SkuUnitInput) =>
                  currentVal + +(unit?.stock || 0),
                0
              )
              if (totalItemInStock > issuedNumber) {
                return helpers.message({
                  custom: tMessage('consoleItemCreate.stock.custom.over'),
                })
              }
              if (totalItemInStock < issuedNumber) {
                return helpers.message({
                  custom: tMessage('consoleItemCreate.stock.custom.shortage'),
                })
              }
              return value
            }),
          order: o.number(),
          thumbnail: o.string().optional().allow(null),
        })
      ),
    }),
    otherwise: o.object().optional(),
  }),
  isDownloadable: o
    .when('type', {
      is: ItemType.Wallpaper,
      then: o.boolean().allow(null).optional(),
      otherwise: o.forbidden(),
    })
    .label(tLabel('consoleItemCreate.isDownloadable')),
})

export const ItemCreateForm: Organism<ItemCreateFormProps> = ({
  resourceType,
  onSubmit,
  fileUploadInfo,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [open, setOpen] = useState<boolean>(false)
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    register,
    setValue,
    watch,
    setError,
    control,
    trigger,
  } = useForm<CreateItemProps>({
    schema,
    defaultValues: {
      ...defaultValues,
      ...defaultResourceValues[resourceType],
      type: resourceType,
    },
  })

  const {
    fields,
    append,
    remove: onRemove,
    move: onMove,
  } = useFieldArray({
    control,
    keyName: '_id',
    name: 'skuSettings.units',
  })

  const handleOnSubmit = useCallback(
    async (values: CreateItemProps) => {
      return onSubmit(values)
        .then(() => {
          showMessage(t('createdItemSuccessful'), 'success')
        })
        .catch(error => {
          handleError<CreateItemProps>(error, setError, showError)
        })
        .finally(() => {
          setOpen(false)
        })
    },
    [onSubmit, setError, showMessage, t, showError]
  )

  return (
    <OrganismFrame sx={{ py: 5 }}>
      {resourceType !== ItemType.Ticket && (
        <ResourceUploadCell
          watch={watch}
          resourceType={resourceType}
          setValue={setValue}
          fileUploadInfo={fileUploadInfo}
          hasCreateResource={true}
          error={
            errors.resourceId ||
            errors.avatarResources?.avatarResourceId ||
            errors.wallpaperResources?.wallpaperResourceId
          }
        />
      )}
      {resourceType === ItemType.Magazine && (
        <PageDirectionInputCell setValue={setValue} watch={watch} />
      )}
      {resourceType === ItemType.Wallpaper && (
        <ToggleInputCell
          content={{
            title: t('isDownloadable'),
            description: t('isDownloadableDesc'),
          }}
          sx={{
            '& > div:last-child span': {
              typography: 'body2',
            },
          }}
          {...register('isDownloadable')}
        />
      )}
      {resourceType === ItemType.Wallpaper && config.modules.eprint && (
        <PrintInputCell
          setValue={setValue}
          isPrintEnabled={watch('metadata.wallpaper.isPrintEnabled')}
          printType={watch('metadata.wallpaper.printType')}
          printCount={watch('metadata.wallpaper.printCount')}
        />
      )}
      {(resourceType === ItemType.Wallpaper ||
        resourceType === ItemType.Avatar) && (
        <ContentsAudioInputCell
          resourceType={resourceType}
          watch={watch}
          setValue={setValue}
          audioUploadInfo={fileUploadInfo}
          hasCreateResource={true}
        />
      )}
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-5759
       */}
      {resourceType === ItemType.Audio && false && (
        <MusicApplicationInputCell />
      )}
      <ItemThumbnailInputCell
        watch={watch}
        resourceType={resourceType}
        setValue={setValue}
        itemUploadInfo={fileUploadInfo}
        error={errors.thumbnail}
        errorBack={errors.thumbnailBack}
      />
      <ItemImageInputCell
        watch={watch}
        setValue={setValue}
        control={control}
        fileUploadInfo={fileUploadInfo}
      />
      <TextInputCell type="text" error={errors.title} {...register('title')} />
      {/* ストア機能または抽選機能がある場合、金額設定の項目を追加 */}
      {(config.modules.store || config.modules.preSale) && (
        <PriceInputCell
          mode="create"
          resourceType={resourceType}
          hasPrice={watch('hasPrice')}
          paymentType={watch('paymentType') ?? UserPaymentType.PrepaidPoint}
          setValue={setValue}
          inputProps={{
            type: 'number',
            error: errors.price,
            ...register('price'),
          }}
        />
      )}
      {resourceType === ItemType.Ticket ? (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.metadata?.ticket?.description}
          {...register('metadata.ticket.description')}
        />
      ) : (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.description}
          {...register('description')}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.metadata?.ticket?.note}
          {...register('metadata.ticket.note')}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <ThroughTicketInputCell
          editable
          isThroughTicket={watch('metadata.ticket.isThroughTicket')}
          control={control}
          setValue={setValue}
          register={register}
          watch={watch}
          errors={errors.metadata?.ticket?.throughTicket}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <ExpirationDateInputCell
          setValue={setValue}
          value={watch('metadata.ticket.expirationTerm')}
          error={errors?.metadata?.ticket?.expirationTerm}
        />
      )}
      {resourceType === ItemType.Merch && (
        <AppDeadlineInputCell
          setValue={setValue}
          error={errors.metadata?.merch?.receptionPeriod?.endedAt}
          value={watch('metadata.merch.receptionPeriod.endedAt')}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <PersonalInformationRequirementsInputCell
          {...register('personalInformationRequirements')}
          personalInformationRequirements={watch(
            'personalInformationRequirements'
          )}
          setValue={setValue}
        />
      )}

      <IssueInputCell
        hasIssuedNumber={watch('hasIssuedNumber')}
        setValue={setValue}
        inputProps={{
          type: 'number',
          error: errors.issuedNumber,
          ...register('issuedNumber'),
          async onChange(event) {
            setValue('issuedNumber', event.target.value as never)
            await Promise.all(
              [...Array(fields.length).keys()].map(idx =>
                trigger(`skuSettings.units.${idx}.stock`)
              )
            )
          },
        }}
      />
      <SkuInputCell
        setValue={setValue}
        hasIssuedNumber={watch('hasIssuedNumber')}
        fields={fields}
        watch={watch}
        trigger={trigger}
        register={register}
        onRemove={onRemove}
        fileUploadInfo={fileUploadInfo}
        onMove={onMove}
        errors={errors.skuSettings}
        onAppend={() =>
          append({
            unitName: '',
            stock: undefined,
            order: 1,
            thumbnail: undefined,
          })
        }
      />
      <LimitIssueInputCell
        hasPurchaseLimitByUser={watch('hasPurchaseLimitByUser')}
        setValue={setValue}
        inputProps={{
          type: 'number',
          error: errors.purchaseLimitByUser,
          ...register('purchaseLimitByUser'),
        }}
      />
      {config.modules.cart && config.modules.pickup && (
        <PerOrderLimitInputCell
          hasPurchaseLimitPerOrder={watch('hasPurchaseLimitPerOrder')}
          setValue={setValue}
          inputProps={{
            type: 'number',
            error: errors.purchaseLimitPerOrder,
            ...register('purchaseLimitPerOrder'),
          }}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <TicketNumberInputCell
          watch={watch}
          setValue={setValue}
          register={register}
          errors={errors?.metadata?.ticket}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <ShareInputCell
          setValue={setValue}
          value={!!watch('metadata.ticket.distributionEnabled')}
          endedAt={watch('metadata.ticket.distributionEndedAt')}
          errors={errors}
        />
      )}
      {config.modules.membership && (
        <ToggleInputCell
          content={{ title: t('membersOnly') }}
          {...register('isPremium')}
        />
      )}
      <ToggleInputCell
        content={{
          title: t('hideSalesNumberLabel'),
          description: t('hideSalesNumberDesc'),
        }}
        sx={{
          '& > div:last-child span': {
            typography: 'body2',
          },
        }}
        checked={watch('hideSalesNumber')}
        {...register('hideSalesNumber')}
      />
      {resourceType === ItemType.Ticket && config.modules.resale && (
        <ResaleInputCell
          setValue={setValue}
          value={!!watch('resaleEnabled', false)}
          startedAt={watch('metadata.ticket.resalePeriod.startedAt')}
          endedAt={watch('metadata.ticket.resalePeriod.endedAt')}
          errors={errors}
        />
      )}
      <Stack spacing={5} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit(() => setOpen(true))}
        >
          {t('submit')}
        </Button>
        {/*
        @note
        下書き機能実装まで非表示
        https://app.clickup.com/t/908127/UT-6521
      <Button
        variant="outlined"
        size="large"
        sx={{ width: 244 }}
        disabled={!isValid || isSubmitting}
      >
        {t('temporarilySave')}
      </Button> */}
      </Stack>
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('confirmSaveTitle')}
        content={t('confirmSaveDesc')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ minWidth: 212 }}
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleSubmit(handleOnSubmit)}
          >
            {t('submit')}
          </Button>
        }
        sx={{
          // 単に props で指定するだけだと
          // default のスタイルが優先されるため
          // セレクタを用いた CSS 指定を行っている
          '& .MuiBackdrop-root': {
            // 画面幅に依らずバックドロップを画面全体にかける
            maxWidth: '100%',
          },
        }}
      />
    </OrganismFrame>
  )
}

const defaultValues: CreateItemProps = {
  title: '',
  type: ItemType.Image,
  description: '',
  isPremium: false,
  personalInformationRequirements: personalInformationRequirementsValue.none,
  price: 0,
  issuedNumber: null,
  hideSalesNumber: true,
  hasPurchaseLimitByUser: false,
  hasPurchaseLimitPerOrder: false,
  purchaseLimitByUser: null,
  purchaseLimitPerOrder: null,
  thumbnail: '',
  copyrights: [],
  images: Array.from(Array(30).keys()).map(() => ({
    tmpPath: '',
    caption: undefined,
  })),
  hasPrice: false,
  hasIssuedNumber: false,
  hasSku: false,
  skuSettings: { units: [] },
  thumbnailType: ItemThumbnailType.Square,
  resource: null,
  avatarResource: null,
  wallpaperResource: null,
  thumbnailResource: null,
  thumbnailBackResource: null,
  paymentType: config.modules.prepaidPoint
    ? UserPaymentType.PrepaidPoint
    : UserPaymentType.Cash,
  isDownloadable: undefined,
  hasAssignedNumber: false,
  isBothThumbnail: false,
}

const defaultResourceValues: Record<ItemType, Partial<CreateItemProps>> = {
  [ItemType.Image]: { resourceId: '' },
  [ItemType.Audio]: { resourceId: '' },
  [ItemType.Merch]: {
    resourceId: '',
    metadata: {
      merch: { receptionPeriod: { endedAt: null } },
    },
    paymentType: config.modules.prepaidPoint
      ? UserPaymentType.PrepaidPoint
      : UserPaymentType.Cash,
  },
  [ItemType.Video]: { resourceId: '' },
  [ItemType.Ticket]: {
    resourceId: '',
    description: null,
    hasPrice: true,
    price: 100,
    resaleEnabled: false,
    metadata: {
      ticket: {
        description: '',
        note: null,
        expirationTerm: null,
        resalePeriod: {
          endedAt: '',
          startedAt: '',
        },
        distributionEnabled: false,
        distributionEndedAt: null,
        assignedNumberPrefix: null,
        startAssignedNumber: null,
        isThroughTicket: false,
        throughTicket: null,
      },
    },
  },
  [ItemType.Magazine]: {
    resourceId: '',
    metadata: {
      magazine: {
        pageDirection: MagazinePageDirection.Left,
      },
    },
    paymentType: config.modules.prepaidPoint
      ? UserPaymentType.PrepaidPoint
      : UserPaymentType.Cash,
  },
  [ItemType.Wallpaper]: {
    wallpaperResources: {
      wallpaperResourceId: '',
    },
    wallpaperResource: null,
    isDownloadable: false,
    metadata: {
      wallpaper: {
        isPrintEnabled: false,
        printCount: null,
        printType: null,
      },
    },
  },
  [ItemType.Avatar]: {
    avatarResources: {
      avatarResourceId: '',
    },
    thumbnailType: ItemThumbnailType.Passthrough,
    avatarResource: null,
  },
}
